import React from 'react'
import type { JSONContent } from '../types'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import { useTheme } from '@mui/material'

interface RenderButtonProps {
  node: JSONContent
}

export function RenderButton({ node }: RenderButtonProps) {
  const attrs = node.attrs || {}
  const {
    text = 'Button',
    url = '#',
    alignment = 'left',
    variant,
    buttonColor,
    textColor,
    borderRadius = 'smooth',
    paddingTop = 10,
    paddingRight = 32,
    paddingBottom = 10,
    paddingLeft = 32,
  } = attrs
  // const buttonStyle = convertStylesToSx(parseStyleString(attrs.style as string))

  const theme = useTheme()

  const DEFAULT_BUTTON_BACKGROUND_COLOR = '#000000'
  const DEFAULT_BUTTON_TEXT_COLOR_CONTAINED = '#ffffff'

  const buttonBackgroundColor =
    buttonColor || theme.palette.primary.main || DEFAULT_BUTTON_BACKGROUND_COLOR

  // Если textColor не задан — берём дефолт в зависимости от variant.
  const resolvedTextColor = React.useMemo(() => {
    if (textColor) {
      return textColor
    }

    switch (variant) {
      case 'contained':
        return DEFAULT_BUTTON_TEXT_COLOR_CONTAINED
      case 'outlined':
        return buttonBackgroundColor
      default:
        return buttonBackgroundColor
    }
  }, [textColor, variant, buttonBackgroundColor])

  const buttonSx = React.useMemo(() => {
    const radiusMap: Record<string, string | number> = {
      sharp: 0,
      smooth: '6px',
      round: '9999px',
    }

    const base = {
      textTransform: 'none' as const,
      fontWeight: 600,
      boxShadow: 'none',
      borderRadius: radiusMap[borderRadius] ?? '6px',
      paddingTop: `${paddingTop}px`,
      paddingBottom: `${paddingBottom}px`,
      paddingLeft: `${paddingLeft}px`,
      paddingRight: `${paddingRight}px`,
    }

    if (variant === 'contained') {
      return {
        // Nest under `&&` to raise specificity above Tailwind preflight,
        // which resets button background/border in this editor.
        '&&': {
          ...base,
          backgroundColor: buttonColor,
          color: resolvedTextColor,
          '&:hover': {
            backgroundColor: buttonColor,
            boxShadow: 'none',
          },
        },
      }
    }

    if (variant === 'outlined') {
      return {
        '&&': {
          ...base,
          color: resolvedTextColor,
          borderColor: buttonColor,
          borderWidth: 1,
          borderStyle: 'solid',
          '&:hover': {
            borderColor: buttonColor,
            backgroundColor: 'transparent',
          },
        },
      }
    }

    // text
    return {
      '&&': {
        ...base,
        color: resolvedTextColor,
        '&:hover': {
          backgroundColor: 'transparent',
        },
      },
    }
  }, [
    variant,
    textColor,
    buttonColor,
    borderRadius,
    paddingTop,
    paddingBottom,
    paddingLeft,
    paddingRight,
  ])

  return (
    <Box
      sx={{
        marginBottom: 2,
        textAlign: alignment as 'left' | 'center' | 'right',
      }}
    >
      {variant}
      <Button
        // variant={variant === 'filled' ? 'contained' : 'outlined'}
        onClick={() => {
          if (url && url !== '#') {
            window.open(url, '_blank')
          }
        }}
        sx={buttonSx}
      >
        {text}
      </Button>
    </Box>
  )
}
